import { inResiOrmStore } from "@superbright/indexeddb-orm";
import type { QueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { queryKeys } from "api/config";
import { APP_BASENAME } from "utils/appConfig";

type UseDebugFeaturesArgs = {
  queryClient: QueryClient;
};

export const useDebugFeatures = ({ queryClient }: UseDebugFeaturesArgs) => {
  const [devToolsVisible, setDevToolsVisible] = useState(false);

  const clearStorageAndDB = useCallback(async () => {
    localStorage.clear();

    const dbs = await indexedDB.databases?.();
    if (dbs) {
      for (const db of dbs) {
        if (db.name) {
          indexedDB.deleteDatabase(db.name);
        }
      }
    } else {
      // Fallback for browsers that don't support indexedDB.databases()
      indexedDB.deleteDatabase("inresi-cache");
    }

    const basePath = APP_BASENAME === "/" ? "" : APP_BASENAME;
    window.location.href = `${basePath}/`;
  }, []);

  // Reset the visitor identity so the next load is treated as a brand-new
  // visitor (clearing the backend "questionnaire completed" redirect). The UUID
  // lives in TWO synced places: the `visitor_uuid` cookie (read by SSR) and the
  // ORM/IndexedDB copy (re-mirrored to the cookie on every load). Both must be
  // cleared or `useVisitorSession` would re-create the cookie from IndexedDB.
  const clearVisitor = useCallback(async () => {
    document.cookie = "visitor_uuid=; Max-Age=0; Path=/";
    try {
      await inResiOrmStore.clearVisitorUUID();
    } catch {
      // ignore — IndexedDB may be unavailable
    }
    queryClient.removeQueries({ queryKey: queryKeys.visitorUUID() });
    // Reload on the property home (strip a trailing /results) so a new session
    // is minted and the home/questionnaire page is shown instead of redirecting.
    const basePath = APP_BASENAME === "/" ? "" : APP_BASENAME;
    const home = window.location.pathname.replace(/\/results\/?$/, "");
    window.location.href = home || `${basePath}/`;
  }, [queryClient]);

  useEffect(() => {
    if (
      process.env.NODE_ENV === "development" &&
      import.meta.env.VITE_DEBUG === "true"
    ) {
      setDevToolsVisible(true);
    }
  }, []);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      const ctrl = event.ctrlKey;
      const alt = event.altKey;
      const key = event.key.toLowerCase() === "r";

      if (ctrl && alt && key) {
        event.preventDefault();

        const iframes = document.getElementsByTagName("iframe");
        for (const iframe of iframes) {
          iframe.contentWindow?.postMessage({ type: "clearStorage" }, "*");
        }

        clearStorageAndDB();
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [clearStorageAndDB]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === "CLEAR_APP_CACHE") {
        indexedDB.deleteDatabase("keyval-store");
        localStorage.clear();
        queryClient.clear();
        const currentUrl = new URL(window.location.href);
        const slug = currentUrl.pathname.split("/").filter(Boolean).pop(); // get last segment

        const basePath = APP_BASENAME === "/" ? "" : APP_BASENAME;
        if (slug) {
          window.location.href = `${basePath}/${slug}`;
          window.parent.location.reload();
        } else {
          window.location.href = `${basePath}/`;
          window.parent.location.reload();
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [queryClient]);

  return { devToolsVisible, clearStorageAndDB, clearVisitor };
};
